import React, { useState } from 'react'
import styles from '../WagesPage.module.css'
import { MONS, isoLocal } from '../timesheets/time'
import { dmy, presets } from './format'
import type { PayPeriod } from './types'

// The pay-period popover (V4 wgCalOpen): From/To boxes, two Monday-first month
// grids with click-anchor range picking, presets, Apply. Nothing reloads until
// Apply, so a half-picked range never queries the API.

interface Props {
  from: string
  to: string
  marked: Set<string> // dates in the loaded period that hold wage rows
  // The tenant's today and pay period (087), for the presets.
  today?: string
  payPeriod?: PayPeriod
  onApply: (from: string, to: string) => void
  onClose: () => void
}

function monthCells(ym: string) {
  const [y, m] = ym.split('-').map(Number)
  const first = new Date(y, m - 1, 1)
  const lead = (first.getDay() + 6) % 7
  const days = new Date(y, m, 0).getDate()
  const cells: (string | null)[] = Array(lead).fill(null)
  for (let d = 1; d <= days; d++) cells.push(isoLocal(new Date(y, m - 1, d)))
  return cells
}

function shiftMonth(ym: string, n: number) {
  const [y, m] = ym.split('-').map(Number)
  const d = new Date(y, m - 1 + n, 1)
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0')
}

export function PeriodPicker({ from, to, marked, today, payPeriod, onApply, onClose }: Props) {
  const [f, setF] = useState(from)
  const [t, setT] = useState(to)
  const [anchor, setAnchor] = useState<string | null>(null)
  const [first, setFirst] = useState(() => shiftMonth(to.slice(0, 7), from.slice(0, 7) === to.slice(0, 7) ? -1 : 0))

  const pick = (iso: string) => {
    if (!anchor) {
      setAnchor(iso)
      setF(iso)
      setT(iso)
    } else {
      setF(anchor < iso ? anchor : iso)
      setT(anchor < iso ? iso : anchor)
      setAnchor(null)
    }
  }

  return (
    <div className={styles.calPop} role="dialog" aria-label="Pay period">
      <div className={styles.calFields}>
        {[
          ['From', f],
          ['To', t],
        ].map(([label, v]) => (
          <div key={label} className={styles.calField}>
            <span className={styles.calFieldLabel}>{label}</span>
            <div className={styles.calBox}>{dmy(v)}</div>
          </div>
        ))}
      </div>
      <div className={styles.calMonths}>
        {[first, shiftMonth(first, 1)].map((ym, i) => {
          const [y, m] = ym.split('-').map(Number)
          return (
            <div key={ym} className={styles.calMonth}>
              <div className={styles.calHead}>
                {i === 0 ? (
                  <button type="button" className={styles.calNav} aria-label="Previous month" onClick={() => setFirst(shiftMonth(first, -1))}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6" /></svg>
                  </button>
                ) : (
                  <span />
                )}
                <span>
                  {MONS[m - 1]} {y}
                </span>
                {i === 1 ? (
                  <button type="button" className={styles.calNav} aria-label="Next month" onClick={() => setFirst(shiftMonth(first, 1))}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6" /></svg>
                  </button>
                ) : (
                  <span />
                )}
              </div>
              <div className={styles.calGrid}>
                {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, k) => (
                  <span key={k} className={styles.calDow}>
                    {d}
                  </span>
                ))}
                {monthCells(ym).map((iso, k) =>
                  iso ? (
                    <button
                      key={iso}
                      type="button"
                      data-date={iso}
                      className={[
                        styles.calDay,
                        iso === f || iso === t ? styles.calEdge : iso > f && iso < t ? styles.calIn : '',
                      ].join(' ')}
                      onClick={() => pick(iso)}
                    >
                      {Number(iso.slice(8))}
                      {marked.has(iso) && <span className={styles.calDot} />}
                    </button>
                  ) : (
                    <span key={'x' + k} />
                  )
                )}
              </div>
            </div>
          )
        })}
      </div>
      <div className={styles.calFoot}>
        {presets(today, payPeriod).map((p) => (
          <button
            key={p.label}
            type="button"
            className={[styles.preset, f === p.from && t === p.to ? styles.presetOn : ''].join(' ')}
            onClick={() => {
              setF(p.from)
              setT(p.to)
              setAnchor(null)
            }}
          >
            {p.label}
          </button>
        ))}
        <span className={styles.grow} />
        <button type="button" className={`${styles.calBtn} ${styles.calCancel}`} onClick={onClose}>
          Cancel
        </button>
        <button type="button" className={`${styles.calBtn} ${styles.calApply}`} onClick={() => onApply(f, t)}>
          Apply
        </button>
      </div>
    </div>
  )
}
